/**
 * SkillCreateSlideout — V2 right-rail skill authoring (G380).
 *
 * Replaces the previous V2 +New Skill Modal that was missing critical
 * V1 functionality:
 *   - Workflow skill type navigated to /canvas and abandoned the wizard
 *   - Connector skill type had no connector picker / Import-from-Connector
 *   - Policy skill type had no doc-upload follow-up
 *   - Standard / Connector / Policy types dropped category, inferenceMode,
 *     guardrails, and connector binding
 *   - No post-create side effects (linkSkillConnector, generateEval)
 *
 * Connector action picker — RESTORED 2026-09-10. The "V2 rebirth UI"
 * (647f5b7) had regressed the connector flow down to a coarse
 * connector-only multi-select: picking a connector no longer listed its
 * actions, so a connector skill was created with NO bound action (not
 * runnable) — V1's SkillFormWithImport (AgentsPage.jsx) showed the
 * connector's operations inline to pick some or all. That inline picker is
 * restored here: select a connector → see its actions (same
 * marketplace-manifest source ImportFromConnectorModalV2 uses) → tick some
 * or all → Create imports each as a properly-bound skill (connectorId +
 * action + endpoint + method), with the Governed-Resume server-execute
 * toggle applied to every imported action. "Import from connector" stays as
 * the equivalent bulk shortcut.
 *
 * This component is the V2 rebuild. It reads from and writes to the SAME
 * V1 backend endpoints (no backend changes):
 *   GET    /api/v1/workflows?agentId={agentId}            ← workflow dropdown
 *   GET    /api/v1/agents/{id}/assigned-connector-ids     ← connector list
 *   POST   /api/v1/agents/{id}/skills                     ← addSkill (primary)
 *   POST   /api/v1/agents/{id}/skills/{skillId}/connectors ← linkSkillConnector
 *   POST   /api/v1/skills/{skillId}/policy-documents      ← upload (post-create)
 *
 * Connector list — IMPORTANT: read from `usePlatform().connectors`, not
 * via a direct `api.getConnectors()` call. V1 (AgentsPage.jsx:381-383)
 * already established this pattern: PlatformContext owns the
 * /api/v1/connectors fetch and flattens the V227 ConnectorResponse
 * wrapper into the legacy flat row shape every consumer expects. The
 * earlier V2 redesign of this slideout regressed by calling
 * `api.getConnectors()` directly — that returned wrapper objects with
 * no top-level `id`/`name`/`type`, so the intersection with
 * `assignedConnectorIds` was always empty and the operator saw a false
 * "No connectors assigned to this agent" banner. Reverted to V1
 * pattern 2026-05-29.
 *
 * Flow (matches V1 SkillFormWithImport semantics, V2 slideout chrome):
 *
 *   Level 1: Type picker (4 cards — standard / workflow / connector / policy)
 *   Level 2: Type-specific config inline below the universal fields
 *            - Workflow → dropdown of agent-linked workflows
 *            - Connector → pick a connector, then tick its actions to import
 *              (each becomes a bound skill); server-execute toggle applies
 *            - Policy → info banner pointing to post-create upload
 *   Level 3: Universal config (name, description, category, risk, autonomy,
 *            inference mode, guardrails)
 *   Level 4: Review summary inline above the Save button
 *
 * Caller can pre-pick a type via `initialType` to skip Level 1 entirely.
 */
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import * as api from '../../../services/api';
import { usePlatform } from '../../../contexts/PlatformContext';
import {
  RefreshCw, ChevronRight, ArrowLeft, CheckCircle2, X, Workflow,
  Database, FileText, Sparkles, Info, Shield, ShieldCheck,
} from 'lucide-react';
import { Button, Pill, Card, Slideout } from '../index';
import { SKILL_TYPES as SKILL_TYPES_REGISTRY } from '../../../constants/skillTypes';
import { SKILL_AUTONOMY_OPTIONS } from '../../../constants/autonomy';
/* Connector ACTION catalogue for the inline per-action picker (V1 parity,
 * restored). Same marketplace-shaped source ImportFromConnectorModalV2 uses
 * (useMarketplaceCatalogue → manifestToMarketplaceConnector), resolved with
 * findMarketplaceForInstalled — NOT a raw manifest fetch here, so the G383
 * "empty list / unmapped rows" regression cannot recur. */
import { useMarketplaceCatalogue } from '../../../data/marketplaceRegistry';
import { findMarketplaceForInstalled } from '../../../data/marketplaceFromRegistry';

/* Resolve iconName strings from the constants module to the actual
 * lucide-react components. Keeping the registry icon-name-only means
 * the constants file has no React dependency and can be safely
 * imported from both v1 and v2 surfaces. */
const SKILL_TYPE_ICONS = {
  Sparkles, Workflow, Database, FileText, ShieldCheck, Shield,
};

/* V2 wizard view of the canonical SKILL_TYPES registry. The label /
 * description / id columns flow directly from the constants module
 * so adding a new kind there lights up the wizard automatically. */
const SKILL_TYPES = SKILL_TYPES_REGISTRY.map((t) => ({
  id:    t.id,
  label: t.label,
  icon:  SKILL_TYPE_ICONS[t.iconName] || Sparkles,
  desc:  t.description,
}));

const CATEGORIES = ['general', 'iam', 'security', 'connectors', 'analytics', 'nlp', 'research'];
const RISK_LEVELS = ['low', 'medium', 'high', 'critical'];
/* DEF-LLC-VOCAB-001 — canonical skill-autonomy vocabulary from the shared
 * module so create / edit / display never drift apart again. */
const AUTONOMY_LEVELS = SKILL_AUTONOMY_OPTIONS;
const INFERENCE_MODES = [
  { value: 'PLATFORM_INFERENCE', label: 'Platform inference' },
  { value: 'SELF_INFERENCE',     label: 'Self inference' },
  { value: 'HUMAN_APPROVAL',     label: 'Human approval' },
];
const GUARDRAIL_OPTIONS = [
  { key: 'rate_limit',        label: 'Rate limit',        desc: 'Throttle calls per minute.' },
  { key: 'audit_log',         label: 'Audit log',         desc: 'Persist every invocation to the audit trail.' },
  { key: 'scope_limit',       label: 'Scope limit',       desc: 'Constrain to declared input scope.' },
  { key: 'approval_required', label: 'Approval required', desc: 'Each execution must be approved.' },
  { key: 'dual_approval',     label: 'Dual approval',     desc: 'Two distinct approvers per call.' },
  { key: 'rbac_check',        label: 'RBAC check',        desc: 'Verify caller has the matching role.' },
];

const DEFAULT_FORM = () => ({
  name: '',
  description: '',
  category: 'general',
  riskLevel: 'low',
  autonomy: 'HUMAN_CONTROLLED',
  inferenceMode: 'PLATFORM_INFERENCE',
  guardrails: [],
  linkedWorkflowId: '',   // workflow_skill only
  connectorIds: [],       // connector_skill (and optionally standard/policy bindings)
  serverExecuteEnabled: false, // connector_skill — complete server-side on approval (Governed Resume)
});

export function SkillCreateSlideout({
  isOpen,
  onClose,
  agentId,
  agentName,
  initialType,          // optional — when the MenuDropdown caller has already chosen
  onCreated,            // called with (createdSkill, payload) after successful create
  addToast,             // toast handler passed in (so we don't import from contexts twice)
}) {
  /* ── Level 1: type pick ──────────────────────────────────────── */
  const [skillType, setSkillType] = useState(initialType || null);

  /* ── Universal + type-specific form fields ───────────────────── */
  const [form, setForm] = useState(DEFAULT_FORM);

  /* ── Type-specific data (loaded on open / type change) ─────────
   * Connectors come from PlatformContext (V1 pattern). Workflows +
   * the per-agent assigned-connector-ids list are still fetched here
   * because they're agent-scoped and not part of the shared platform
   * context. */
  const { connectors: platformConnectors = [], loadConnectors } = usePlatform();
  /* Marketplace-shaped catalogue → per-connector action list (see import note above). */
  const { connectors: marketplaceConnectors = [] } = useMarketplaceCatalogue();
  const [linkedWorkflows, setLinkedWorkflows] = useState([]);
  const [assignedConnectorIds, setAssignedConnectorIds] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  /* Connector-skill action picker (V1 parity, restored 2026-09-10): the
   * connector whose actions are being browsed, and the set of actions the
   * operator has ticked. When ≥1 action is picked, Create imports each as a
   * properly-bound connector skill (connectorId + action + endpoint + method). */
  const [pickConnectorId, setPickConnectorId] = useState(null);
  const [pickActionIds, setPickActionIds] = useState(() => new Set());

  /* ── Saving state ────────────────────────────────────────────── */
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  /* Reset when the slideout opens (or when the caller pre-picks a type). */
  useEffect(() => {
    if (!isOpen) return;
    setSkillType(initialType || null);
    setForm(DEFAULT_FORM());
    setError(null);
    setPickConnectorId(null);
    setPickActionIds(new Set());
  }, [isOpen, initialType]);

  /* Hydrate agent-scoped data sources. Connectors come from
   * PlatformContext, so we only fetch workflows + the agent's
   * assigned-connector-id list here. If PC's connector list is empty
   * on first open (e.g. operator deep-linked to this agent without
   * having visited the Connectors page first), kick off a fetch — PC
   * publishes the result and our memo below picks it up automatically. */
  useEffect(() => {
    if (!isOpen || !agentId) return;
    let cancelled = false;
    setDataLoading(true);
    if (platformConnectors.length === 0 && typeof loadConnectors === 'function') {
      loadConnectors({});
    }
    (async () => {
      try {
        const [wfRes, connIdRes] = await Promise.all([
          api.getWorkflows?.({ agentId }).catch(() => null),
          api.getAssignedConnectorIds?.(agentId).catch(() => null),
        ]);
        if (cancelled) return;
        const wfList = Array.isArray(wfRes?.data) ? wfRes.data
                     : Array.isArray(wfRes?.data?.items) ? wfRes.data.items
                     : Array.isArray(wfRes) ? wfRes : [];
        setLinkedWorkflows(wfList);
        const connIds = Array.isArray(connIdRes?.data) ? connIdRes.data
                      : Array.isArray(connIdRes?.data?.connectorIds) ? connIdRes.data.connectorIds
                      : Array.isArray(connIdRes) ? connIdRes : [];
        setAssignedConnectorIds(connIds);
      } finally {
        if (!cancelled) setDataLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, agentId, platformConnectors.length, loadConnectors]);

  /* Subset of connectors that are actually assigned to this agent.
   * Mirrors V1 AgentsPage.jsx:381-383 — reads from PlatformContext
   * (already-flattened) so `c.id` is the real connector id, not an
   * undefined wrapper field. */
  const availableConnectors = useMemo(() => {
    if (!assignedConnectorIds.length) return [];
    const idSet = new Set(assignedConnectorIds.map(String));
    return platformConnectors.filter(c => idSet.has(String(c.id)));
  }, [platformConnectors, assignedConnectorIds]);

  /* The connector whose actions are on screen, its manifest, and the
   * manifest's operations — resolved exactly as ImportFromConnectorModalV2
   * does, so the two flows list identical actions. */
  const pickConnector = useMemo(
    () => availableConnectors.find(c => String(c.id) === String(pickConnectorId)) || null,
    [availableConnectors, pickConnectorId]
  );
  const pickManifest = useMemo(() => {
    if (!pickConnector || marketplaceConnectors.length === 0) return null;
    return findMarketplaceForInstalled(marketplaceConnectors, pickConnector);
  }, [pickConnector, marketplaceConnectors]);
  const pickActions = useMemo(
    () => (pickManifest && Array.isArray(pickManifest.skills)) ? pickManifest.skills : [],
    [pickManifest]
  );

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const toggleGuardrail = (key) => setForm(prev => ({
    ...prev,
    guardrails: prev.guardrails.includes(key)
      ? prev.guardrails.filter(g => g !== key)
      : [...prev.guardrails, key],
  }));
  const toggleConnector = (cid) => setForm(prev => ({
    ...prev,
    connectorIds: prev.connectorIds.includes(cid)
      ? prev.connectorIds.filter(c => c !== cid)
      : [...prev.connectorIds, cid],
  }));
  const toggleAction = (aid) => setPickActionIds(prev => {
    const next = new Set(prev);
    if (next.has(aid)) next.delete(aid); else next.add(aid);
    return next;
  });
  const selectAllActions = () => setPickActionIds(new Set(pickActions.map(a => a.id || a.name)));
  const selectNoActions = () => setPickActionIds(new Set());

  /* Connector-skill import mode: operator has ticked ≥1 connector action.
   * Each imported skill takes its name/description from the action template,
   * so the single name/description fields don't apply in this mode. */
  const importMode = skillType === 'connector_skill' && pickActionIds.size > 0;

  /* Validation gate for the Save button. */
  const validation = useMemo(() => {
    if (!skillType) return 'Pick a skill type to continue.';
    if (importMode) return null; // names come from the picked actions
    if (!form.name.trim()) return 'Skill name is required.';
    if (!form.description.trim()) return 'Description is required so reviewers know what the skill does.';
    if (skillType === 'workflow_skill' && !form.linkedWorkflowId) {
      return linkedWorkflows.length === 0
        ? 'No workflows linked to this agent yet. Link a workflow in Team → Assignments → Workflow assignments first.'
        : 'Pick a workflow to bind this skill to.';
    }
    return null;
  }, [skillType, form, linkedWorkflows.length, importMode]);

  const handleSave = useCallback(async () => {
    if (validation) {
      setError(validation);
      return;
    }
    setSaving(true);
    setError(null);
    try {
      /* ── Connector-skill import path (V1 parity, restored) ─────────────
       * When the operator has picked one or more connector actions, create
       * each as a properly-bound connector skill — mirroring
       * ImportFromConnectorModalV2's payload (endpoint / method / params /
       * baseUrl / credentialType / requiredScopes / samplePayload) so
       * ConnectorExecutionService dispatches it at runtime with no extra
       * wiring. The server-execute toggle + guardrails from the form apply to
       * every imported skill; connector linking + eval seeding mirror the
       * import modal step-for-step. */
      if (skillType === 'connector_skill' && pickActionIds.size > 0 && pickConnector) {
        const chosen = pickActions.filter(a => pickActionIds.has(a.id || a.name));
        const results = [];
        let firstCreated = null;
        for (const op of chosen) {
          const endpoint = op.endpoint || op.path || '';
          const method = (op.method || 'GET').toUpperCase();
          const displayName = op.displayName || op.name || (op.id || '');
          const opPayload = {
            name: op.name || displayName,
            description: `${op.description || displayName} [${method} ${endpoint}]`,
            category: form.category || 'connectors',
            riskLevel: op.riskLevel || form.riskLevel || 'low',
            autonomy: form.autonomy || 'HUMAN_CONTROLLED',
            inferenceMode: form.inferenceMode || 'PLATFORM_INFERENCE',
            guardrails: form.guardrails,
            skillType: 'connector_skill',
            endpoint: endpoint || null,
            httpMethod: method,
            baseUrl: pickManifest?.baseUrl || pickConnector.url || null,
            params: op.params || null,
            credentialType: op.credentialType || pickManifest?.authType || pickConnector.authMethod || null,
            requiredScopes: op.requiredScopes || null,
            samplePayload: op.samplePayload || null,
            accessDescription: op.accessDescription || `Calls ${method} ${endpoint} on ${pickConnector.name}`,
            serverExecuteEnabled: !!form.serverExecuteEnabled,
          };
          try {
            const cRes = await api.addSkill?.(agentId, opPayload);
            if (cRes && cRes.ok === false) { results.push({ name: opPayload.name, ok: false }); continue; }
            const created = cRes?.data || cRes;
            const sid = created?.id || created?.skillId;
            if (!firstCreated) firstCreated = created;
            if (sid) {
              await api.linkSkillConnector?.(agentId, sid, { connectorId: pickConnector.id, purpose: 'data_source' }).catch(() => null);
              await api.generateEval?.(agentId, { skillId: sid }).catch(() => null);
            }
            results.push({ name: opPayload.name, ok: true });
          } catch {
            results.push({ name: opPayload.name, ok: false });
          }
        }
        const okN = results.filter(r => r.ok).length;
        addToast?.({
          tone: okN === results.length ? 'success' : 'warning',
          title: okN === results.length
            ? `Imported ${okN} ${okN === 1 ? 'skill' : 'skills'} from ${pickConnector.name}`
            : `Imported ${okN} of ${results.length} — ${results.length - okN} failed`,
          description: 'Run an eval next so each skill can pass the Build → Decide gate.',
        });
        onCreated?.(firstCreated, { imported: results });
        onClose?.();
        return;
      }

      /* Payload matches V1 contract verbatim — AgentsPage.jsx:398-407. */
      const payload = {
        name: form.name.trim(),
        description: form.description.trim(),
        category: form.category,
        riskLevel: form.riskLevel,
        autonomy: form.autonomy,
        inferenceMode: form.inferenceMode,
        guardrails: form.guardrails,
        skillType,
        linkedResourceId: skillType === 'workflow_skill' ? form.linkedWorkflowId : null,
        policyConfig: null,
        // Governed Resume — only a connector skill can be completed server-side.
        serverExecuteEnabled: skillType === 'connector_skill' ? !!form.serverExecuteEnabled : false,
      };

      const res = await api.addSkill?.(agentId, payload);
      if (res && res.ok === false) {
        const msg = res.error?.message || res.error || 'Create failed';
        throw new Error(typeof msg === 'string' ? msg : 'Create failed');
      }
      const createdSkill = res?.data || res;
      const newSkillId = createdSkill?.id || createdSkill?.skillId;

      /* Post-create: link selected connectors. V1's handleAddSkill iterates
       * the picked connector list and POSTs `{connectorId, purpose}` for
       * each (AgentsPage.jsx:417-418). G381 fix: previously we were passing
       * the raw connectorId string as the payload, which backend rejected
       * with 400 ("Path or query parameter 'connectorId' missing"). */
      if (newSkillId && form.connectorIds.length > 0) {
        await Promise.all(form.connectorIds.map((connectorId) =>
          api.linkSkillConnector?.(agentId, newSkillId, {
            connectorId,
            purpose: 'data_source',
          }).catch(() => null)
        ));
      }

      addToast?.({
        tone: 'success',
        title: `Skill "${payload.name}" added`,
        description: skillType === 'policy_skill'
          ? 'Upload the policy document next from the skill detail panel.'
          : skillType === 'workflow_skill'
          ? 'Workflow linked. Run an eval next so this skill can pass the Build → Decide gate.'
          : skillType === 'wasm_skill'
          /* P0-2 — wasm_skill needs a separate file upload after row
             creation; the skill detail editor mounts WasmSkillUploader. */
          ? 'Upload the compiled .wasm binary next from the skill detail panel (sandbox memory + time limits configurable there).'
          : 'Run an eval next so this skill can pass the Build → Decide gate.',
      });

      onCreated?.(createdSkill, payload);
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Could not create the skill.');
      addToast?.({ tone: 'danger', title: 'Could not add skill', description: err?.message });
    } finally {
      setSaving(false);
    }
  }, [validation, skillType, form, agentId, onCreated, onClose, addToast,
      pickConnector, pickManifest, pickActions, pickActionIds]);

  const handleBack = () => {
    /* L2 → L1: keep the form draft so the operator doesn't lose data when
     * exploring type options. */
    setSkillType(null);
    setError(null);
  };

  /* Header / footer rendering. ─────────────────────────────────── */
  const headerTitle = !skillType
    ? 'New skill'
    : `New ${(SKILL_TYPES.find(t => t.id === skillType)?.label || '').toLowerCase()} skill`;

  const footer = !skillType ? (
    <div className="flex items-center justify-end gap-2">
      <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
    </div>
  ) : (
    <div className="flex items-center justify-between gap-2">
      <Button variant="ghost" size="sm" iconLeft={<ArrowLeft size={13} />} onClick={handleBack} disabled={saving || !!initialType}>
        Back to types
      </Button>
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
        <Button variant="primary" onClick={handleSave} disabled={saving || !!validation}
          iconLeft={saving ? null : <CheckCircle2 size={13} />}>
          {saving ? 'Creating…' : 'Create skill'}
        </Button>
      </div>
    </div>
  );

  return (
    <Slideout
      isOpen={isOpen}
      onClose={saving ? undefined : onClose}
      width={620}
      ariaLabel={headerTitle}
      header={
        <div>
          <div className="flex items-center gap-2 mb-2">
            <Sparkles size={14} className="text-v2-action-strong" />
            <Pill tone="muted" size="sm">{agentName || 'Agent'}</Pill>
            {skillType && <Pill tone="cyan" size="sm">{SKILL_TYPES.find(t => t.id === skillType)?.label}</Pill>}
          </div>
          <h2 className="text-[16px] font-bold m-0">{headerTitle}</h2>
          <p className="text-[12px] text-v2-ink-secondary mt-1">
            {!skillType
              ? 'Pick the kind of skill to author. Each type opens its own configuration on the next step.'
              : 'Configure the skill below. Universal fields apply to every skill type; the section above is type-specific.'}
          </p>
        </div>
      }
      footer={footer}
    >
      {/* ─── Level 1: Type picker ─────────────────────────────── */}
      {!skillType && (
        <ul className="space-y-2 m-0 p-0 list-none">
          {SKILL_TYPES.map((opt) => {
            const Icon = opt.icon;
            return (
              <li key={opt.id}>
                <button
                  type="button"
                  onClick={() => setSkillType(opt.id)}
                  className="v2-focus-ring w-full text-left rounded-v2-md border border-v2-border-subtle p-3 hover:border-v2-action hover:bg-v2-action-soft/40 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 rounded-v2-md bg-v2-action-soft flex items-center justify-center text-v2-action-strong flex-shrink-0">
                      <Icon size={16} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <strong className="text-v2-ink text-[13px]">{opt.label} skill</strong>
                        <ChevronRight size={13} className="text-v2-ink-muted ml-auto" />
                      </div>
                      <p className="text-[11.5px] text-v2-ink-muted mt-0.5 m-0">{opt.desc}</p>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {/* ─── Level 2–3: Type-specific + universal config ──────── */}
      {skillType && (
        <div className="space-y-4">
          {error && (
            <div className="rounded-v2-md border border-v2-danger-border bg-v2-danger-soft px-3 py-2 text-[12px] text-v2-danger">
              {error}
            </div>
          )}

          {/* Type-specific block (Level 2) */}
          <Card padding="sm">
            <Card.Header
              title={`${SKILL_TYPES.find(t => t.id === skillType)?.label} configuration`}
              actions={dataLoading
                ? <Pill tone="muted" size="sm">Loading…</Pill>
                : null}
            />
            <Card.Body>
              {skillType === 'standard' && (
                <p className="text-[12.5px] text-v2-ink-secondary m-0">
                  Standard skills are hand-authored. After create, run an eval against
                  the skill so it can pass the Build → Decide gate.
                </p>
              )}

              {skillType === 'workflow_skill' && (
                /* The headline fix — V1 dropdown sourced from getWorkflows({agentId}).
                 * Replaces the previous V2 behaviour of navigating to /canvas. */
                <div className="space-y-2">
                  {linkedWorkflows.length === 0 ? (
                    <div className="rounded-v2-md border border-v2-warning-border bg-v2-warning-soft px-3 py-2 text-[12px] text-v2-warning-strong flex items-start gap-2">
                      <Info size={14} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">No workflows linked to this agent yet.</div>
                        <div className="mt-0.5">Link a workflow in <em>Team → Assignments → Workflow assignments</em> first, then return here to bind it as a skill.</div>
                      </div>
                    </div>
                  ) : (
                    <label className="block">
                      <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Linked workflow *</span>
                      <select value={form.linkedWorkflowId} onChange={(e) => set({ linkedWorkflowId: e.target.value })}
                        className="v2-focus-ring w-full text-[12.5px] border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
                        <option value="">Pick a workflow…</option>
                        {linkedWorkflows.map((w) => (
                          <option key={w.id} value={w.id}>{w.name || w.id}{w.status ? ` · ${w.status}` : ''}</option>
                        ))}
                      </select>
                      <span className="block text-[11px] text-v2-ink-muted mt-1">
                        Sourced from <code className="font-mono-v2">GET /api/v1/workflows?agentId={agentId?.slice(0,8)}…</code>
                      </span>
                    </label>
                  )}
                </div>
              )}

              {skillType === 'connector_skill' && (
                <div className="space-y-3">
                  <p className="text-[12.5px] text-v2-ink-secondary m-0">
                    Pick a connector assigned to this agent, then choose the actions to add.
                    Each selected action becomes its own connector skill, bound to that
                    action's endpoint + method. Only connectors assigned to this agent are
                    listed. (The <em>Import from connector</em> button is the same picker as a
                    quick bulk shortcut.)
                  </p>
                  {availableConnectors.length === 0 ? (
                    <div className="rounded-v2-md border border-v2-warning-border bg-v2-warning-soft px-3 py-2 text-[12px] text-v2-warning-strong flex items-start gap-2">
                      <Info size={14} className="flex-shrink-0 mt-0.5" />
                      <div>
                        <div className="font-semibold">No connectors assigned to this agent.</div>
                        <div className="mt-0.5">Grant connector access in <em>Team → Assignments → Agent assignments → Manage access</em> first.</div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid gap-2" style={{ gridTemplateColumns: '190px 1fr' }}>
                      {/* Connector list — single-select to browse its actions */}
                      <div className="border border-v2-border-subtle rounded-v2-sm overflow-hidden flex flex-col" style={{ maxHeight: 260 }}>
                        <div className="flex-1 overflow-y-auto">
                          {availableConnectors.map(c => {
                            const sel = String(c.id) === String(pickConnectorId);
                            return (
                              <button
                                key={c.id}
                                type="button"
                                onClick={() => { setPickConnectorId(c.id); setPickActionIds(new Set()); }}
                                className={
                                  'w-full text-left px-2.5 py-2 border-b border-v2-border-subtle last:border-b-0 text-[12px] transition-colors ' +
                                  (sel ? 'bg-v2-cyan-soft text-v2-ink' : 'bg-v2-surface text-v2-ink-secondary hover:bg-v2-muted')
                                }
                              >
                                <div className="font-semibold truncate">{c.name || c.id}</div>
                                <div className="text-[10.5px] text-v2-ink-muted truncate">{c.type || c.authMethod || '—'}</div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                      {/* Actions of the selected connector — pick some or all */}
                      <div className="border border-v2-border-subtle rounded-v2-sm overflow-hidden flex flex-col" style={{ maxHeight: 260 }}>
                        {!pickConnector ? (
                          <div className="flex-1 flex items-center justify-center text-[12px] text-v2-ink-muted p-4 text-center">
                            Pick a connector to see its available skills.
                          </div>
                        ) : !pickManifest ? (
                          <div className="flex-1 flex items-center justify-center text-[12px] text-v2-ink-muted p-4 text-center">
                            No manifest found for <span className="font-semibold mx-1">{pickConnector.name}</span> — this connector predates the registry. Author a skill manually with the fields below.
                          </div>
                        ) : pickActions.length === 0 ? (
                          <div className="flex-1 flex items-center justify-center text-[12px] text-v2-ink-muted p-4 text-center">
                            Manifest declares no importable actions.
                          </div>
                        ) : (
                          <>
                            <div className="flex items-center gap-2 px-3 py-2 border-b border-v2-border-subtle bg-v2-muted/30">
                              <span className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold">
                                {pickActions.length} actions · {pickActionIds.size} selected
                              </span>
                              <div className="ml-auto flex items-center gap-1">
                                <button type="button" onClick={selectAllActions} className="text-[10.5px] text-v2-trust hover:underline">All</button>
                                <span className="text-v2-ink-muted text-[10px]">·</span>
                                <button type="button" onClick={selectNoActions} className="text-[10.5px] text-v2-trust hover:underline">None</button>
                              </div>
                            </div>
                            <div className="flex-1 overflow-y-auto">
                              {pickActions.map(a => {
                                const aid = a.id || a.name;
                                const checked = pickActionIds.has(aid);
                                return (
                                  <label
                                    key={aid}
                                    className={
                                      'flex items-start gap-2 px-3 py-2 border-b border-v2-border-subtle last:border-b-0 cursor-pointer ' +
                                      (checked ? 'bg-v2-cyan-soft/30' : 'hover:bg-v2-muted')
                                    }
                                  >
                                    <input type="checkbox" className="mt-0.5" checked={checked} onChange={() => toggleAction(aid)} />
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-[12px] font-medium text-v2-ink truncate">{a.displayName || a.name || aid}</span>
                                        {a.method && <Pill tone="muted" size="sm">{a.method}</Pill>}
                                      </div>
                                      <div className="text-[10.5px] text-v2-ink-muted mt-0.5 line-clamp-2">{a.description || a.endpoint || '—'}</div>
                                      {a.endpoint && <div className="text-[10px] text-v2-ink-muted font-mono-v2 mt-1 truncate">{a.endpoint}</div>}
                                    </div>
                                  </label>
                                );
                              })}
                            </div>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                  <label className="flex items-start gap-2 rounded-v2-md border border-v2-trust-border bg-v2-trust-soft px-3 py-2 cursor-pointer">
                    <input
                      type="checkbox"
                      className="mt-0.5 flex-shrink-0"
                      checked={!!form.serverExecuteEnabled}
                      onChange={(e) => setForm(prev => ({ ...prev, serverExecuteEnabled: e.target.checked }))}
                    />
                    <span className="text-[12px] text-v2-ink-secondary">
                      <span className="font-semibold text-v2-trust-strong flex items-center gap-1">
                        <ShieldCheck size={13} className="flex-shrink-0" /> Complete server-side on approval
                      </span>
                      <span className="mt-0.5 block">
                        When a protected call of these skills is approved, the platform completes
                        it itself and records the result — the agent never has to poll or come
                        back. Applies to every action selected above. Enable only for skills the
                        agent fires without awaiting a result.
                      </span>
                    </span>
                  </label>
                </div>
              )}

              {skillType === 'policy_skill' && (
                <div className="rounded-v2-md border border-v2-trust-border bg-v2-trust-soft px-3 py-2 text-[12px] text-v2-trust-strong flex items-start gap-2">
                  <FileText size={14} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Policy documents are uploaded after create.</div>
                    <div className="mt-0.5">Create the skill first, then upload your policy PDFs / docs from the skill detail panel. For framework-bound policies, use <em>Policies → Add Rule</em>.</div>
                  </div>
                </div>
              )}

              {/* P0-2 — wasm_skill creation guidance. The .wasm binary
                  is uploaded after the row is created (same pattern as
                  policy_skill above): WasmSkillUploader mounts in the
                  skill detail editor on AgentDetailPageV2 and requires
                  a skillId. */}
              {skillType === 'wasm_skill' && (
                <div className="rounded-v2-md border border-v2-success/30 bg-v2-success-soft px-3 py-2 text-[12px] text-v2-success-strong flex items-start gap-2">
                  <ShieldCheck size={14} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="font-semibold">Sandboxed Wasm execution.</div>
                    <div className="mt-0.5">
                      Create the skill first, then upload your compiled <code className="font-mono-v2 text-[11px]">.wasm</code> binary (max 8 MiB)
                      from the skill detail panel. The runtime enforces hard memory + wall-clock caps, denies all
                      host imports by default, and audits every host crossing.
                      See <code className="font-mono-v2 text-[11px]">docs/WASM_SKILL_DEVELOPER_GUIDE.md</code> for the host ABI.
                    </div>
                  </div>
                </div>
              )}
            </Card.Body>
          </Card>

          {/* Universal fields (Level 3) */}
          <Card padding="sm">
            <Card.Header title="Skill metadata" />
            <Card.Body>
              <div className="grid grid-cols-2 gap-3 text-[12.5px]">
                <label className="block col-span-2">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Name *</span>
                  <input type="text" value={form.name} onChange={(e) => set({ name: e.target.value })}
                    placeholder={skillType === 'connector_skill' ? 'e.g. okta.assignGroup' : skillType === 'policy_skill' ? 'e.g. PHIRedactionPolicy' : 'e.g. classifyAlert'}
                    autoFocus
                    className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
                </label>
                <label className="block col-span-2">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Description *</span>
                  <textarea value={form.description} onChange={(e) => set({ description: e.target.value })}
                    rows={2}
                    placeholder="What does this skill do? Reviewers will see this in eval and approval surfaces."
                    className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface" />
                </label>
                <label className="block">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Category</span>
                  <select value={form.category} onChange={(e) => set({ category: e.target.value })}
                    className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Risk level</span>
                  <select value={form.riskLevel} onChange={(e) => set({ riskLevel: e.target.value })}
                    className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
                    {RISK_LEVELS.map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Autonomy</span>
                  <select value={form.autonomy} onChange={(e) => set({ autonomy: e.target.value })}
                    className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
                    {AUTONOMY_LEVELS.map(a => <option key={a.value} value={a.value}>{a.label}</option>)}
                  </select>
                </label>
                <label className="block">
                  <span className="block text-[10.5px] uppercase tracking-wider text-v2-ink-muted font-semibold mb-1">Inference mode</span>
                  <select value={form.inferenceMode} onChange={(e) => set({ inferenceMode: e.target.value })}
                    className="v2-focus-ring w-full border border-v2-border rounded-v2-md px-2.5 py-1.5 bg-v2-surface">
                    {INFERENCE_MODES.map(i => <option key={i.value} value={i.value}>{i.label}</option>)}
                  </select>
                </label>
              </div>
            </Card.Body>
          </Card>

          {/* Guardrails */}
          <Card padding="sm">
            <Card.Header title="Guardrails" actions={
              <Pill tone={form.guardrails.length > 0 ? 'success' : 'muted'} size="sm">
                {form.guardrails.length} selected
              </Pill>
            } />
            <Card.Body>
              <p className="text-[11.5px] text-v2-ink-muted mb-2 m-0">
                Constrain how this skill can be invoked. Enable conservatively — each
                guardrail trades runtime flexibility for safety.
              </p>
              <div className="grid grid-cols-2 gap-2">
                {GUARDRAIL_OPTIONS.map(g => (
                  <label key={g.key} className="flex items-start gap-2 text-[12px] cursor-pointer rounded-v2-md border border-v2-border-subtle p-2 hover:bg-v2-muted/30">
                    <input type="checkbox"
                      checked={form.guardrails.includes(g.key)}
                      onChange={() => toggleGuardrail(g.key)}
                      className="mt-0.5 flex-shrink-0" />
                    <div>
                      <strong className="text-v2-ink">{g.label}</strong>
                      <div className="text-[10.5px] text-v2-ink-muted mt-0.5">{g.desc}</div>
                    </div>
                  </label>
                ))}
              </div>
            </Card.Body>
          </Card>

          {/* Connector binding (universal — present for every type EXCEPT
              connector_skill, whose own action picker above already selects +
              binds the connector per imported action). Populated only when the
              agent has assigned connectors. V1 wires this for the other types. */}
          {skillType !== 'connector_skill' && availableConnectors.length > 0 && (
            <Card padding="sm">
              <Card.Header title="Connector access" actions={
                <Pill tone={form.connectorIds.length > 0 ? 'cyan' : 'muted'} size="sm">
                  {form.connectorIds.length} of {availableConnectors.length}
                </Pill>
              } />
              <Card.Body>
                <p className="text-[11.5px] text-v2-ink-muted mb-2 m-0">
                  Bind this skill to one or more connectors. Each pick fires a separate
                  <code className="font-mono-v2"> POST /api/v1/agents/{'{id}'}/skills/{'{skillId}'}/connectors</code>
                  after the skill is created.
                </p>
                <div className="max-h-[220px] overflow-y-auto space-y-1.5 pr-1">
                  {availableConnectors.map(c => (
                    <label key={c.id} className="flex items-center gap-2 text-[12px] cursor-pointer rounded-v2-md border border-v2-border-subtle bg-v2-surface px-2.5 py-1.5 hover:bg-v2-muted/30">
                      <input type="checkbox"
                        checked={form.connectorIds.includes(c.id)}
                        onChange={() => toggleConnector(c.id)} />
                      <span className="font-semibold text-v2-ink">{c.name || c.id}</span>
                      {c.type && <span className="text-v2-ink-muted text-[10.5px]"> · {c.type}</span>}
                    </label>
                  ))}
                </div>
              </Card.Body>
            </Card>
          )}

          {/* Validation hint */}
          {validation && (
            <div className="rounded-v2-md border border-v2-warning-border bg-v2-warning-soft px-3 py-2 text-[12px] text-v2-warning-strong flex items-center gap-2">
              <Shield size={13} />
              {validation}
            </div>
          )}
        </div>
      )}
    </Slideout>
  );
}

export default SkillCreateSlideout;
